import type * as React from 'react';

import { DimeComboboxSeparator } from '../DimeCombobox.js';
import type { DimeAutocompleteFooterAction } from '../lib/dime-autocomplete-footer-action.js';

// Matches a list row, since the action sits under the options as one more row.
const ACTION_ROW =
  'relative flex w-full cursor-default items-center rounded-md py-1 pr-8 pl-1.5 text-left text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground';

type DimeAutocompleteFooterProps = Readonly<{
  /** The field's input, which Tab, Shift+Tab and Escape on the action return to. */
  fieldId: string;
  notice?: React.ReactNode;
  action?: DimeAutocompleteFooterAction;
  actionRef?: React.Ref<HTMLButtonElement>;
  onClose: () => void;
}>;

/** What sits under a `DimeAutocomplete` list: a notice, such as why the list is cut short, then an action such as "+ New Contact". */
export function DimeAutocompleteFooter({
  fieldId,
  notice,
  action,
  actionRef,
  onClose,
}: DimeAutocompleteFooterProps) {
  return (
    <>
      {/* Mounted while the list is, so a notice that arrives later is announced. */}
      <div
        role="status"
        className={notice == null ? undefined : 'px-2 py-1.5 text-sm text-muted-foreground'}
      >
        {notice}
      </div>
      {action && (
        <div className="p-1 pt-0">
          <DimeComboboxSeparator className="mx-0" />
          <button
            ref={actionRef}
            type="button"
            className={ACTION_ROW}
            onKeyDown={(event) => {
              if (event.key !== 'Tab' && event.key !== 'Escape') return;
              const field = event.currentTarget.ownerDocument.getElementById(fieldId);
              // Tab moves on from the field, as the list sits at the end of the page where it would fall off.
              if (event.key === 'Tab' && !event.shiftKey) {
                onClose();
                field?.focus();
                return;
              }
              event.preventDefault();
              if (event.key === 'Escape') onClose();
              field?.focus();
            }}
            // Keeps focus in the input, so the popup's blur-close doesn't unmount this row before the click lands.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onClose();
              action.onSelect();
            }}
          >
            {action.label}
          </button>
        </div>
      )}
    </>
  );
}
